import { AddLibraryItemCard } from '@/components/AddLibraryItemCard';
import { AGENT_TILE_WIDTH } from '@/components/AgentTile';
import { CreateAgentBanner } from '@/components/AppBanner/CreateAgentBanner';
import { LibrarySkeleton } from '@/components/Library/LibrarySkeleton';
import { useNavigateToAgentsEditor } from '@/hooks/navigation/useNavigateToAgentsEditor';
import { EmbedAgentModal, RemoveEmbedAgentModal } from '@/pages/agents/components/modals';
import { useGetAllVoices, usePollingStatus } from '@/pages/agents/hooks';
import { useAuth } from '@/services/auth';
import { useClientKey } from '@/state/agent';
import { IAgent } from '@d-id/types';
import styled from '@emotion/styled';
import { useState } from 'react';
import { useAgentsLibrary } from '../hooks';
import { AgentsList } from './list';
import { TemplatesList } from './list/TemplatesList';

const StyledAddLibraryItemCard = styled(AddLibraryItemCard)`
    width: 250px;
    height: 350px;
`;

const BannerContainer = styled.div`
    margin: 32px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 21rem;
    box-sizing: border-box;
`;

const StyledLibrarySkeleton = styled(LibrarySkeleton)`
    margin-top: 72px;
`;

interface Props {
    onDelete: (agent: IAgent) => void;
    onShare: (agent: IAgent) => void;
    onRemoveEmbed: (agent: IAgent) => void;
    onAgentUpgrade: (agent: IAgent) => void;
    isOpenModal: boolean;
    setEmbedModalState: (state: boolean) => void;
}

export const Library = ({ onDelete, onShare, onRemoveEmbed, onAgentUpgrade, isOpenModal, setEmbedModalState }: Props) => {
    const { isAnonymous } = useAuth();
    const { embedAgents, nonEmbedAgents, publicAgents, privateAgents, isLoading } = useAgentsLibrary();
    const [removeEmbedModalAgent, setRemoveEmbedModalAgent] = useState<IAgent>();
    const { navigateToAgentEditor } = useNavigateToAgentsEditor();
    const { data: clientKey } = useClientKey({ isAnonymous });

    useGetAllVoices();

    usePollingStatus(privateAgents);

    const onEmbedClick = (agent: IAgent) => {
        onShare(agent);
    };

    if (isLoading || (!clientKey && !isAnonymous)) {
        return (
            <StyledLibrarySkeleton amount={11} tileHeight={`calc(${AGENT_TILE_WIDTH}px * (9 / 16))`} tileWidth={`${AGENT_TILE_WIDTH}px`} />
        );
    }

    if (!privateAgents?.length && !publicAgents?.length) {
        return (
            <>
                <BannerContainer>
                    <CreateAgentBanner />
                </BannerContainer>
                <StyledAddLibraryItemCard
                    id="create-agent-card"
                    label="Create agent"
                    onClick={() => navigateToAgentEditor({ mixPanelFrom: 'agent-library' })}
                />
            </>
        );
    }

    return (
        <>
            {!!publicAgents.length && <TemplatesList agents={publicAgents} clientKey={clientKey} />}
            {!!embedAgents.length && (
                <AgentsList
                    agents={embedAgents}
                    onDelete={onDelete}
                    onAgentUpgrade={onAgentUpgrade}
                    onEmbed={onEmbedClick}
                    onRemoveEmbed={onRemoveEmbed}
                    onEdit={agent => {
                        if (agent.embed) {
                            setRemoveEmbedModalAgent(agent);
                        } else {
                            navigateToAgentEditor({ agentId: agent.id, mixPanelFrom: 'agent-library' });
                        }
                    }}
                    title="Embeddable Agents"
                    clientKey={clientKey}
                />
            )}
            <AgentsList
                agents={nonEmbedAgents}
                onDelete={onDelete}
                onAgentUpgrade={onAgentUpgrade}
                onEmbed={onEmbedClick}
                onRemoveEmbed={onRemoveEmbed}
                onEdit={agent => {
                    if (agent.embed) {
                        setRemoveEmbedModalAgent(agent);
                    } else {
                        navigateToAgentEditor({ agentId: agent.id, mixPanelFrom: 'agent-library' });
                    }
                }}
                title="My Agents"
                clientKey={clientKey}
            />
            <EmbedAgentModal isOpen={isOpenModal} onClose={() => setEmbedModalState(false)} isMoreAgents={!!embedAgents.length} />
            <RemoveEmbedAgentModal
                agent={removeEmbedModalAgent}
                isOpen={!!removeEmbedModalAgent}
                onClose={() => setRemoveEmbedModalAgent(undefined)}
            />
        </>
    );
};
